/* eslint-disable react-hooks/immutability */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Clock, Printer, Store, Loader2, RotateCcw, Info, Pause, Lock } from 'lucide-react';import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { AdminNavbar } from '../../components/custom/AdminNavbar';
import HolidayScheduleManager from '../../components/admin/HolidayScheduleManager';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default settings
const DEFAULT_SETTINGS = {
  restaurant_name: 'Crispy Chicken Forfar',
  open_hour: 16,
  open_minute: 0,
  close_hour: 23,
  close_minute: 0,
  preorder_start_hour: 11,
  preorder_start_minute: 0,
  preparation_time: 60,
  collection_time: 30,
  service_charge: 0.75,
  printer_ip: '',
  printer_port: 9100,
  auto_print_enabled: false,
  accept_cod: true,
  min_order_amount: 10.00
};

const AdminSettings = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [hasChanges, setHasChanges] = useState(false);

  // "Stop taking orders today" state lives in a separate collection so
  // it auto-clears at next UK midnight without admin action.
  const [pause, setPause] = useState({ paused: false, message: '' });
  const [pauseSaving, setPauseSaving] = useState(false);

  // Refund passcode safety-net state. We never load the actual passcode
  // back from the server — we only know whether one is configured.
  const [refundPasscode, setRefundPasscode] = useState({
    configured: false,
    new_value: '',
    saving: false,
  });

  // POS PIN state — mirrors the refund-passcode pattern. The POS device
  // (Android WebView wrapper) signs in with this PIN.
  const [posPin, setPosPin] = useState({
    configured: false,
    new_value: '',
    saving: false,
  });

  useEffect(() => {
    if (authLoading) return; // wait for auth context to hydrate token → user
    if (!user || !user.is_admin) {
      navigate('/admin/login');
      return;
    }
    fetchSettings();
    fetchPause();
    fetchRefundPasscode();
    fetchPosPin();
  }, [user, authLoading, navigate]);

  const fetchRefundPasscode = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/admin/refund-passcode`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRefundPasscode((p) => ({ ...p, configured: !!r.data.configured }));
    } catch (err) {
      console.error('Failed to fetch refund passcode state:', err);
    }
  };

  const fetchPosPin = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/admin/pos-pin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosPin((p) => ({ ...p, configured: !!r.data.configured }));
    } catch (err) {
      console.error('Failed to fetch POS PIN state:', err);
    }
  };

  const savePosPin = async (pin) => {
    setPosPin((p) => ({ ...p, saving: true }));
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API_URL}/api/admin/pos-pin`,
        { pin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosPin({ configured: !!r.data.configured, new_value: '', saving: false });
      toast.success(pin === '' ? 'POS PIN removed' : 'POS PIN saved');
    } catch (err) {
      console.error('Failed to save POS PIN:', err);
      toast.error(err.response?.data?.detail || 'Failed to save PIN');
      setPosPin((p) => ({ ...p, saving: false }));
    }
  };

  const saveRefundPasscode = async (passcode) => {
    setRefundPasscode((p) => ({ ...p, saving: true }));
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API_URL}/api/admin/refund-passcode`,
        { passcode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefundPasscode({
        configured: !!r.data.configured,
        new_value: '',
        saving: false,
      });
      toast.success(
        passcode === ''
          ? 'Refund passcode removed'
          : 'Refund passcode saved'
      );
    } catch (err) {
      console.error('Failed to save refund passcode:', err);
      toast.error(err.response?.data?.detail || 'Failed to save passcode');
      setRefundPasscode((p) => ({ ...p, saving: false }));
    }
  };

  const fetchPause = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/admin/pause-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPause({ paused: !!r.data.paused, message: r.data.message || '' });
    } catch (err) {
      console.error('Failed to fetch pause state:', err);
    }
  };

  const togglePause = async (next) => {
    setPauseSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API_URL}/api/admin/pause-orders`,
        { paused: next, message: pause.message || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPause({ paused: !!r.data.paused, message: r.data.message || '' });
      toast.success(next ? 'Orders paused for today' : 'Orders resumed');
    } catch (err) {
      console.error('Failed to toggle pause:', err);
      toast.error('Failed to update');
    } finally {
      setPauseSaving(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && Object.keys(response.data).length > 0) {
        setSettings({ ...DEFAULT_SETTINGS, ...response.data });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Use defaults if no settings exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const resetToDefaults = () => {
    if (window.confirm('Reset all settings to defaults?')) {
      setSettings(DEFAULT_SETTINGS);
      setHasChanges(true);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-background">
      <AdminNavbar title="Restaurant Settings" />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin/dashboard">
            <Button variant="ghost">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={resetToDefaults}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Defaults
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges || saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pause Orders Today */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl p-6 shadow-sm border ${
                pause.paused
                  ? 'bg-red-50 border-red-200'
                  : 'bg-white border-transparent'
              }`}
              data-testid="pause-orders-card"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    pause.paused ? 'bg-red-100' : 'bg-primary/10'
                  }`}>
                    <Pause className={`h-5 w-5 ${
                      pause.paused ? 'text-red-600' : 'text-primary'
                    }`} />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">Stop Taking Orders Today</h2>
                    <p className="text-xs text-muted-foreground">
                      {pause.paused
                        ? "Orders are paused. Customers see a 'closed' banner. Auto-resumes at UK midnight."
                        : "Flip this on to immediately close the website for new orders for the rest of today (UK)."}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={pause.paused}
                  onCheckedChange={togglePause}
                  disabled={pauseSaving}
                  data-testid="pause-orders-switch"
                />
              </div>

              {pause.paused && (
                <div className="mt-4 space-y-2">
                  <Label htmlFor="pause_message">Message shown to customers (optional)</Label>
                  <Input
                    id="pause_message"
                    value={pause.message}
                    onChange={(e) => setPause(p => ({ ...p, message: e.target.value }))}
                    onBlur={() => togglePause(true)}
                    placeholder="e.g. We're not taking orders right now. Back tomorrow."
                    data-testid="pause-orders-message-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Click outside the box to save the message.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Refund Passcode */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
              data-testid="refund-passcode-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Lock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Refund Passcode</h2>
                  <p className="text-xs text-muted-foreground">
                    Required to confirm every refund. Stops accidental clicks from actually refunding a customer.
                  </p>
                </div>
              </div>

              <div className="mt-4 text-xs">
                {refundPasscode.configured ? (
                  <span className="inline-block px-2 py-0.5 rounded bg-green-100 text-green-800"
                    data-testid="refund-passcode-status">
                    Passcode is set. Refunds will require it.
                  </span>
                ) : (
                  <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800"
                    data-testid="refund-passcode-status">
                    No passcode set. Refunds will go through immediately.
                  </span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <Label htmlFor="refund_passcode_input">
                    {refundPasscode.configured ? 'Change passcode' : 'Set passcode'} (4 digits, numbers only)
                  </Label>
                  <Input
                    id="refund_passcode_input"
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    maxLength={4}
                    autoComplete="new-password"
                    value={refundPasscode.new_value}
                    onChange={(e) => {
                      // Strip everything except digits and cap at 4 characters.
                      const clean = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setRefundPasscode((p) => ({ ...p, new_value: clean }));
                    }}
                    placeholder="••••"
                    className="mt-1.5 tracking-widest"
                    data-testid="refund-passcode-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => saveRefundPasscode(refundPasscode.new_value)}
                    disabled={
                      refundPasscode.saving || refundPasscode.new_value.length !== 4
                    }
                    data-testid="refund-passcode-save"
                  >
                    {refundPasscode.saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                  {refundPasscode.configured && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (window.confirm('Remove the refund passcode? Refunds will no longer require confirmation.')) {
                          saveRefundPasscode('');
                        }
                      }}
                      disabled={refundPasscode.saving}
                      data-testid="refund-passcode-remove"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>

            {/* POS PIN */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
              data-testid="pos-pin-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Lock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">POS PIN</h2>
                  <p className="text-xs text-muted-foreground">
                    4-digit code cashiers enter on the POS device to sign in. Rotate whenever staff changes.
                  </p>
                </div>
              </div>

              <div className="mt-4 text-xs">
                {posPin.configured ? (
                  <span
                    className="inline-block px-2 py-0.5 rounded bg-green-100 text-green-800"
                    data-testid="pos-pin-status"
                  >
                    PIN is set. POS device can sign in.
                  </span>
                ) : (
                  <span
                    className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800"
                    data-testid="pos-pin-status"
                  >
                    No PIN set. POS login is disabled until you set one.
                  </span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <Label htmlFor="pos_pin_input">
                    {posPin.configured ? 'Change PIN' : 'Set PIN'} (4 digits, numbers only)
                  </Label>
                  <Input
                    id="pos_pin_input"
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    maxLength={4}
                    autoComplete="new-password"
                    value={posPin.new_value}
                    onChange={(e) => {
                      const clean = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setPosPin((p) => ({ ...p, new_value: clean }));
                    }}
                    placeholder="••••"
                    className="mt-1.5 tracking-widest"
                    data-testid="pos-pin-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => savePosPin(posPin.new_value)}
                    disabled={posPin.saving || posPin.new_value.length !== 4}
                    data-testid="pos-pin-save"
                  >
                    {posPin.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                  {posPin.configured && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (window.confirm('Remove the POS PIN? The POS device will not be able to sign in until you set a new one.')) {
                          savePosPin('');
                        }
                      }}
                      disabled={posPin.saving}
                      data-testid="pos-pin-remove"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Restaurant Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Store className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-lg font-bold">Restaurant Information</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="restaurant_name">Restaurant Name</Label>
                  <Input
                    id="restaurant_name"
                    value={settings.restaurant_name}
                    onChange={(e) => handleChange('restaurant_name', e.target.value)}
                    className="mt-1.5"
                    data-testid="restaurant-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="service_charge">Service Charge (£)</Label>
                    <Input
                      id="service_charge"
                      type="number"
                      step="0.01"
                      min="0"
                      value={settings.service_charge}
                      onChange={(e) => handleChange('service_charge', parseFloat(e.target.value) || 0)}
                      className="mt-1.5"
                      data-testid="service-charge-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="min_order">Minimum Order (£)</Label>
                    <Input
                      id="min_order"
                      type="number"
                      step="0.01"
                      min="0"
                      value={settings.min_order_amount}
                      onChange={(e) => handleChange('min_order_amount', parseFloat(e.target.value) || 0)}
                      className="mt-1.5"
                      data-testid="min-order-input"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div>
                    <Label htmlFor="accept_cod">Accept Cash on Delivery</Label>
                    <p className="text-sm text-muted-foreground">Allow customers to pay with cash</p>
                  </div>
                  <Switch
                    id="accept_cod"
                    checked={settings.accept_cod}
                    onCheckedChange={(checked) => handleChange('accept_cod', checked)}
                    data-testid="accept-cod-switch"
                  />
                </div>
              </div>
            </motion.div>

            {/* Operating Hours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-lg font-bold">Operating Hours</h2>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    Hours are in UK time (Europe/London). Preorders are accepted before opening time.
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="preorder_start">Preorder Opens</Label>
                  <div className="flex items-center mt-1.5 gap-1">
                    <Input
                      id="preorder_start"
                      type="number"
                      min="0"
                      max="23"
                      value={settings.preorder_start_hour}
                      onChange={(e) => handleChange('preorder_start_hour', parseInt(e.target.value) || 0)}
                      className="w-16"
                      data-testid="preorder-start-input"
                    />
                    <span className="text-muted-foreground">:</span>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      value={settings.preorder_start_minute ?? 0}
                      onChange={(e) => handleChange('preorder_start_minute', Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                      className="w-16"
                      data-testid="preorder-start-minute-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="open_hour">Restaurant Opens</Label>
                  <div className="flex items-center mt-1.5 gap-1">
                    <Input
                      id="open_hour"
                      type="number"
                      min="0"
                      max="23"
                      value={settings.open_hour}
                      onChange={(e) => handleChange('open_hour', parseInt(e.target.value) || 0)}
                      className="w-16"
                      data-testid="open-hour-input"
                    />
                    <span className="text-muted-foreground">:</span>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      value={settings.open_minute ?? 0}
                      onChange={(e) => handleChange('open_minute', Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                      className="w-16"
                      data-testid="open-minute-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="close_hour">Restaurant Closes</Label>
                  <div className="flex items-center mt-1.5 gap-1">
                    <Input
                      id="close_hour"
                      type="number"
                      min="0"
                      max="23"
                      value={settings.close_hour}
                      onChange={(e) => handleChange('close_hour', parseInt(e.target.value) || 0)}
                      className="w-16"
                      data-testid="close-hour-input"
                    />
                    <span className="text-muted-foreground">:</span>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      value={settings.close_minute ?? 0}
                      onChange={(e) => handleChange('close_minute', Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                      className="w-16"
                      data-testid="close-minute-input"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="prep_time">Preparation Time — Delivery (minutes)</Label>
                  <Input
                    id="prep_time"
                    type="number"
                    min="15"
                    max="180"
                    value={settings.preparation_time}
                    onChange={(e) => handleChange('preparation_time', parseInt(e.target.value) || 60)}
                    className="mt-1.5 w-32"
                    data-testid="prep-time-input"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Shown at checkout for delivery orders
                  </p>
                </div>
                <div>
                  <Label htmlFor="collection_time">Collection Time (minutes)</Label>
                  <Input
                    id="collection_time"
                    type="number"
                    min="5"
                    max="120"
                    value={settings.collection_time ?? 30}
                    onChange={(e) => handleChange('collection_time', parseInt(e.target.value) || 30)}
                    className="mt-1.5 w-32"
                    data-testid="collection-time-input"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Shown at checkout for collection orders
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Holiday Schedules */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
              data-testid="holiday-schedules-section"
            >
              <HolidayScheduleManager />
            </motion.div>

            {/* Printer Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Printer className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-lg font-bold">Network Printer (ESC/POS)</h2>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    For server-side printing. For browser-based printing, use{' '}
                    <Link to="/admin/printer-settings" className="underline font-medium">
                      QZ Tray Settings
                    </Link>.
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="printer_ip">Printer IP Address</Label>
                    <Input
                      id="printer_ip"
                      placeholder="192.168.1.100"
                      value={settings.printer_ip}
                      onChange={(e) => handleChange('printer_ip', e.target.value)}
                      className="mt-1.5"
                      data-testid="printer-ip-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="printer_port">Port</Label>
                    <Input
                      id="printer_port"
                      type="number"
                      value={settings.printer_port}
                      onChange={(e) => handleChange('printer_port', parseInt(e.target.value) || 9100)}
                      className="mt-1.5"
                      data-testid="printer-port-input"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div>
                    <Label htmlFor="auto_print">Auto-Print New Orders</Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically print receipts when orders are paid
                    </p>
                  </div>
                  <Switch
                    id="auto_print"
                    checked={settings.auto_print_enabled}
                    onCheckedChange={(checked) => handleChange('auto_print_enabled', checked)}
                    data-testid="auto-print-switch"
                  />
                </div>
              </div>
            </motion.div>

            {/* Save Button (Mobile) */}
            <div className="md:hidden">
              <Button 
                onClick={handleSave} 
                disabled={!hasChanges || saving}
                className="w-full"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
